"use client";
import { useRouter as useNextRouter, useParams } from "next/navigation";
import { startPageTransition } from "@/utils/pageLoader.utils";

interface RouterOptions {
  autoHideLoading?: boolean;
  /**
   * Safety-net duration (ms) for the auto-hide timer — only matters when
   * `autoHideLoading` is true. Defaults to startPageTransition's own 9s.
   * Callers that keep the loader up for a full SSR navigation (rather than
   * relying on the 9s default) should pass a generous value here instead of
   * disabling autoHideLoading outright — otherwise a failed/aborted
   * navigation leaves the full-screen, click-blocking overlay up forever
   * (see MLC-2522 PR #2087 review).
   */
  autoHideDuration?: number;
  scroll?: boolean;
}

const useCRouter = () => {
  const router = useNextRouter();
  const { locale } = useParams();

  const push = (path: string, options?: RouterOptions, refresh: boolean = false) => {
    const { autoHideLoading = true, autoHideDuration, ...rest } = options || {};
    startPageTransition(autoHideLoading, autoHideDuration);
    router.push(`/${locale}${path}`, rest);
    if (refresh) {
      router.refresh();
    }
  };

  return {
    ...router,
    nextPush: router.push,
    push,
  };
};

export default useCRouter;
